/**
 * Hero block — progressive video enhancement, and the phone slider.
 *
 * The server renders only <img>. This module upgrades qualifying tiles to
 * <video> or an embed iframe. With JS disabled nothing here runs and the
 * poster stands, which is the whole point.
 *
 * On a phone the mosaic becomes a slider: the headline stays at the top and
 * every tile becomes one full-width slide, poster only — the video upgrade is
 * gated at 1024px and never runs there (canUpgrade). See initSlider().
 */

import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';

const GATE_MIN_WIDTH = 1024;
const PAUSE_KEY = 'dnHeroPaused';

/**
 * Below this the hero is a slider. 782 is WordPress's own columns breakpoint
 * and the one blocks/_hero.scss already switches the mosaic at, so the
 * stylesheet and the script change state at the same width.
 */
const SLIDER_MAX_WIDTH = 781;

/**
 * Read the pause state persisted by a previous page view.
 *
 * @return {boolean} True when the visitor previously paused.
 */
function readPersistedPause() {
	try {
		return '1' === sessionStorage.getItem( PAUSE_KEY );
	} catch {
		return false;
	}
}

/*
 * Single source of truth for the pause state, seeded from the previous page
 * view. Held in memory rather than re-read from sessionStorage because writes
 * throw in private browsing — a reader that always answered false there would
 * leave the button unable to toggle at all.
 */
let heroPaused = readPersistedPause();

/**
 * Current pause state.
 *
 * Consulted by the upgrade path so a tile that upgrades while the visitor
 * has already paused never starts playing in the first place — see
 * upgradeVideo() and upgradeEmbed().
 *
 * @return {boolean} True when playback is paused.
 */
function isPaused() {
	return heroPaused;
}

/**
 * Should this visitor get video at all?
 *
 * navigator.connection is Chrome/Edge only. Optional chaining yields
 * undefined in Safari and Firefox, which passes both connection checks —
 * those users are not punished for a missing non-standard API. Only an
 * explicit slow-connection or Data Saver signal blocks the upgrade.
 *
 * @return {boolean} True when video may load.
 */
function canUpgrade() {
	const conn = navigator.connection;

	return (
		window.matchMedia( `(min-width: ${ GATE_MIN_WIDTH }px)` ).matches &&
		! window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches &&
		conn?.saveData !== true &&
		! [ 'slow-2g', '2g' ].includes( conn?.effectiveType )
	);
}

/**
 * Replace a tile's poster with an autoplaying muted video.
 *
 * @param {HTMLElement} tile Tile element carrying data-video-src.
 */
function upgradeVideo( tile ) {
	const src = tile.dataset.videoSrc;
	if ( ! src ) {
		return;
	}

	const poster = tile.querySelector( 'img.hero__media' );
	const video = document.createElement( 'video' );

	video.className = 'hero__media hero__video';
	video.src = src;
	video.muted = true;
	// Never autoplay a tile the visitor already paused — asking it to play
	// only to immediately pause() races the play() promise and can reject
	// it with AbortError, which tears the element right back out below.
	video.autoplay = ! isPaused();
	video.loop = true;
	video.playsInline = true;
	// Spec §11 asks for preload="none" until a tile intersects. That is
	// achieved by construction: this element does not exist until the
	// IntersectionObserver fires, so 'auto' here means "now that you are
	// actually on screen, load", not "load on page init".
	video.preload = 'auto';
	video.setAttribute( 'aria-hidden', 'true' );
	video.tabIndex = -1;

	// The focal point needs no carrying across: it is two custom properties on
	// the TILE, and this video inherits them the same way the poster does.
	// This used to copy poster.style.objectPosition, which was right while the
	// poster carried one inline — and silently copied an empty string the
	// moment it stopped, leaving every upgraded video centred whatever its
	// focal point said. Measured on the home page: heads cropped at the top of
	// tiles set to focal_y 0 and 10.
	if ( poster ) {
		video.poster = poster.currentSrc || poster.src;
	}

	tile.appendChild( video );
	tile.classList.add( 'hero__tile--upgraded' );

	// The tile stays upgraded and paused underneath the poster — leave it
	// there for the pause control to resume rather than ever calling play().
	if ( isPaused() ) {
		return;
	}

	// A rejected play() is fine — the poster is still underneath, so the
	// video is simply left in place, paused. Removing the element here would
	// permanently sink the tile: init() already called observer.unobserve()
	// on it, and start() only ever begins observing, so a torn-down tile
	// could never upgrade again for this page view. Clicking Pause while the
	// initial play() promise is still pending is exactly the case that
	// rejects with AbortError in Chrome — leaving the element intact lets
	// Resume call play() on it again.
	const attempt = video.play();
	if ( attempt && typeof attempt.catch === 'function' ) {
		attempt.catch( () => {} );
	}
}

/**
 * Build the provider-specific embed URL.
 *
 * enablejsapi=1 is not cosmetic — without it the pause control cannot reach
 * a YouTube player via postMessage.
 *
 * @param {string} provider 'youtube' or 'vimeo'.
 * @param {string} id       Video ID.
 * @return {string} Fully-qualified embed URL.
 */
function buildEmbedSrc( provider, id ) {
	if ( 'youtube' === provider ) {
		const params = new URLSearchParams( {
			autoplay: '1',
			mute: '1',
			loop: '1',
			playlist: id, // Required for loop=1 on a single video.
			controls: '0',
			playsinline: '1',
			modestbranding: '1',
			rel: '0',
			enablejsapi: '1',
		} );
		return `https://www.youtube-nocookie.com/embed/${ id }?${ params }`;
	}

	// Vimeo's background=1 bundles autoplay, mute, loop and chrome removal.
	const params = new URLSearchParams( {
		background: '1',
		autoplay: '1',
		muted: '1',
		loop: '1',
	} );
	return `https://player.vimeo.com/video/${ id }?${ params }`;
}

/**
 * Send the current pause/play command to one embed iframe.
 *
 * Shared by setPaused() (broadcast to every embed) and upgradeEmbed() (catch
 * up a single, newly-created embed with the state that already applies).
 *
 * @param {HTMLIFrameElement} iframe Embed iframe.
 * @param {boolean}           paused True to pause.
 */
function postEmbedCommand( iframe, paused ) {
	const isVimeo = 'vimeo' === iframe.closest( '.hero__tile' )?.dataset.embedProvider;
	const message = isVimeo
		? JSON.stringify( { method: paused ? 'pause' : 'play' } )
		: JSON.stringify( {
				event: 'command',
				func: paused ? 'pauseVideo' : 'playVideo',
				args: '',
		  } );
	const origin = isVimeo
		? 'https://player.vimeo.com'
		: 'https://www.youtube-nocookie.com';
	iframe.contentWindow?.postMessage( message, origin );
}

/**
 * Replace a tile's poster with an autoplaying muted embed.
 *
 * Iframes ignore object-fit, so the element is oversized past the tile and
 * centred by CSS instead. See _hero.scss.
 *
 * @param {HTMLElement} tile Tile carrying data-embed-provider and data-embed-id.
 */
function upgradeEmbed( tile ) {
	const { embedProvider: provider, embedId: id } = tile.dataset;
	if ( ! provider || ! id ) {
		return;
	}

	const iframe = document.createElement( 'iframe' );
	iframe.className = 'hero__embed';
	iframe.src = buildEmbedSrc( provider, id );
	iframe.title = '';
	iframe.setAttribute( 'aria-hidden', 'true' );
	iframe.tabIndex = -1;
	iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
	iframe.setAttribute( 'frameborder', '0' );
	iframe.loading = 'lazy';

	tile.appendChild( iframe );
	tile.classList.add( 'hero__tile--upgraded' );

	// The embed URL always autoplays by construction (buildEmbedSrc is not
	// ours to change here), so a tile upgrading late while the visitor has
	// already paused must be told to pause the moment its player is ready —
	// otherwise it plays unpaused even though the control still reads
	// "paused". Wait for load so the provider's player script has had a
	// chance to attach its postMessage listener.
	if ( isPaused() ) {
		iframe.addEventListener( 'load', () => postEmbedCommand( iframe, isPaused() ), { once: true } );
	}
}

/**
 * Dispatch a tile to the right upgrade path based on its data-hero-media
 * attribute, then reveal the pause control once the first tile upgrades.
 *
 * @param {HTMLElement} tile Tile element.
 */
function upgradeTile( tile ) {
	if ( tile.classList.contains( 'hero__tile--upgraded' ) ) {
		return;
	}
	if ( 'video' === tile.dataset.heroMedia ) {
		upgradeVideo( tile );
	} else if ( 'embed' === tile.dataset.heroMedia ) {
		upgradeEmbed( tile );
	}

	if ( tile.classList.contains( 'hero__tile--upgraded' ) ) {
		initPauseControl();
	}
}

/**
 * Observe tiles and upgrade them as they scroll into view.
 *
 * The gate is re-evaluated on a debounced resize so a window widened past the
 * breakpoint starts serving video. Downgrading is deliberately out of scope —
 * `start()` is idempotent and only ever begins observing, never tears down.
 */
function init() {
	const tiles = document.querySelectorAll( '.hero__tile[data-hero-media]' );
	if ( ! tiles.length ) {
		return;
	}

	let observer = null;

	function start() {
		if ( observer || ! canUpgrade() ) {
			return;
		}

		observer = new IntersectionObserver(
			( entries ) => {
				entries.forEach( ( entry ) => {
					if ( entry.isIntersecting ) {
						upgradeTile( entry.target );
						observer.unobserve( entry.target );
					}
				} );
			},
			{ rootMargin: '200px' }
		);

		tiles.forEach( ( tile ) => observer.observe( tile ) );
	}

	start();

	let resizeTimer;
	window.addEventListener( 'resize', () => {
		window.clearTimeout( resizeTimer );
		resizeTimer = window.setTimeout( start, 250 );
	} );
}

/**
 * Point the pause control at whatever it will do next.
 *
 * Paused playback means the button now offers "Play", and vice versa — the
 * label names the action, not the state. Both strings are translated
 * server-side and carried on the element; see the button in render.php.
 */
function syncPauseControl() {
	const button = document.querySelector( '.hero__pause' );
	if ( ! button ) {
		return;
	}

	const label = button.querySelector( '.hero__pause-label' );
	if ( label ) {
		label.textContent = heroPaused
			? button.dataset.labelPlay
			: button.dataset.labelPause;
	}

	button.classList.toggle( 'hero__pause--paused', heroPaused );
}

/**
 * Pause or resume every upgraded tile.
 *
 * @param {boolean} paused True to pause.
 */
function setPaused( paused ) {
	heroPaused = paused;

	document.querySelectorAll( '.hero__video' ).forEach( ( video ) => {
		if ( paused ) {
			video.pause();
		} else {
			video.play().catch( () => {} );
		}
	} );

	document.querySelectorAll( '.hero__embed' ).forEach( ( iframe ) => {
		postEmbedCommand( iframe, paused );
	} );

	try {
		sessionStorage.setItem( PAUSE_KEY, paused ? '1' : '0' );
	} catch {
		// Private browsing — the control still works for this page view.
	}

	syncPauseControl();
}

/**
 * Reveal and wire the pause control. Called once a tile has upgraded.
 */
function initPauseControl() {
	const button = document.querySelector( '.hero__pause' );
	if ( ! button || button.dataset.ready ) {
		return;
	}

	button.dataset.ready = 'true';
	button.hidden = false;

	if ( isPaused() ) {
		// Carries the state forward from a previous page view, and labels the
		// button on the way through.
		setPaused( true );
	} else {
		syncPauseControl();
	}

	button.addEventListener( 'click', () => setPaused( ! isPaused() ) );
}

/**
 * The phone slider.
 *
 * Below SLIDER_MAX_WIDTH the mosaic's tiles are lifted out of the grid into a
 * Splide track placed straight after the headline, so the headline leads and
 * every tile is one full-width slide. Above it, everything goes back exactly
 * where it was and the grid is the grid again. The grid's original children,
 * in order, are the whole record of "where it was": putting the mosaic back
 * is appending them again in that order, which moves each one home.
 *
 * Nothing about the tiles changes — the poster, the focal-point properties,
 * an optional link, a <video> that was upgraded on a wider screen — they are
 * the same elements in a different parent. A video is paused for the slider
 * and let go again on the way back, so a phone never runs one under a slide.
 */
const sliders = new WeakMap();

/**
 * @param {HTMLElement} hero The block root.
 */
function mountHeroSlider( hero ) {
	if ( sliders.has( hero ) ) {
		return;
	}

	const grid = hero.querySelector( '.hero__grid' );
	const tiles = grid ? Array.from( grid.querySelectorAll( ':scope > .wp-block-damelio-network-2026-wordpress-theme-media-tile' ) ) : [];

	if ( ! grid || tiles.length < 2 ) {
		return;
	}

	const original = Array.from( grid.children );

	const root = document.createElement( 'div' );
	root.className = 'hero__slider splide';
	root.setAttribute( 'aria-label', hero.dataset.sliderLabel || '' );

	const track = document.createElement( 'div' );
	track.className = 'splide__track';

	const list = document.createElement( 'div' );
	list.className = 'splide__list';

	tiles.forEach( ( tile ) => {
		tile.classList.add( 'splide__slide' );
		list.appendChild( tile );

		tile.querySelectorAll( 'video' ).forEach( ( video ) => video.pause() );
	} );

	track.appendChild( list );
	root.appendChild( track );

	// After the headline when there is one, else first.
	const heading = grid.querySelector( ':scope > .wp-block-heading' );

	if ( heading ) {
		heading.after( root );
	} else {
		grid.prepend( root );
	}

	const reduced = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	const splide = new Splide( root, {
		type: 'loop',
		perPage: 1,
		gap: '10px',
		arrows: true,
		pagination: true,
		keyboard: 'focused',
		speed: reduced ? 0 : 400,
		// Splide draws its own arrows and dots inside the root; these class
		// names are what blocks/_hero.scss styles. Its default arrow SVG is
		// kept — it takes currentColor.
		classes: {
			arrows: 'splide__arrows hero__slider-arrows',
			arrow: 'splide__arrow hero__slider-arrow',
			prev: 'splide__arrow--prev hero__slider-arrow--prev',
			next: 'splide__arrow--next hero__slider-arrow--next',
			pagination: 'splide__pagination hero__slider-dots',
			page: 'splide__pagination__page hero__slider-dot',
		},
		i18n: {
			prev: hero.dataset.labelPrev || 'Previous',
			next: hero.dataset.labelNext || 'Next',
		},
	} );

	splide.mount();
	sliders.set( hero, { splide, grid, original } );
	hero.classList.add( 'hero--sliding' );
}

/**
 * @param {HTMLElement} hero The block root.
 */
function unmountHeroSlider( hero ) {
	const entry = sliders.get( hero );

	if ( ! entry ) {
		return;
	}

	const { splide, grid, original } = entry;

	// destroy(true) removes the clones, the inline sizes and every class it
	// added — but not the splide__slide class this file put on, which goes
	// below, and not the root, which is ours.
	splide.destroy( true );

	original.forEach( ( child ) => {
		child.classList.remove( 'splide__slide' );
		grid.appendChild( child );

		if ( ! isPaused() ) {
			child.querySelectorAll( 'video' ).forEach( ( video ) => {
				video.play().catch( () => {} );
			} );
		}
	} );

	const root = grid.querySelector( ':scope > .hero__slider' );

	if ( root ) {
		root.remove();
	}

	sliders.delete( hero );
	hero.classList.remove( 'hero--sliding' );
}

/**
 * Mount or unmount every hero to match the viewport — on load, and whenever
 * the width crosses the line.
 */
function initSlider() {
	const heroes = document.querySelectorAll( '.hero' );

	if ( ! heroes.length ) {
		return;
	}

	const phone = window.matchMedia( `(max-width: ${ SLIDER_MAX_WIDTH }px)` );

	const apply = () => {
		heroes.forEach( ( hero ) => {
			if ( phone.matches ) {
				mountHeroSlider( hero );
			} else {
				unmountHeroSlider( hero );
			}
		} );
	};

	apply();
	phone.addEventListener( 'change', apply );
}

function boot() {
	initSlider();
	init();
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', boot );
} else {
	boot();
}

export { canUpgrade, upgradeTile };
